/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React from 'react'
import { format } from 'date-fns'
import CheckIcon from '../../../assets/Featured-icon-outline.svg'
import InfoIcon from '../../../assets/info-icon.svg'
import { Heading, Wrapper, SubHeading } from './styles'
import { SlotStatus, type ISlot } from '../../../models/DistributionPlan'
import Box from '@mui/material/Box'
import { grey, yellow } from '../../../colors'
import { getSlotSections } from '../../../store/slices/distributionPlanSlice/selectors'

interface CourseEvaluationAlertProps {
  slot: ISlot
}

interface AlertContent {
  icon: string
  title: string
  subHeading?: string
}

const SlotStatusComponent: React.FC<CourseEvaluationAlertProps> = ({ slot }) => {
  const status = slot?.slotNodeDetails?.slotStatus
  const slotSections = getSlotSections()
  const getFormattedDate = (date: string | null): string | any => {
    return date != null ? format(new Date(date), "MMMM d, yyyy 'at' h:mmaa") : null
  }

  const renderAlert = ({ icon, title, subHeading }: AlertContent): JSX.Element => (
    <Wrapper status={status}>
      <img src={icon} alt={`${icon} Icon`} />
      <Box padding={1}>
        <Heading color={status === SlotStatus.Draft ? yellow[500] : grey[950]}>{title}</Heading>
        {(subHeading != null) && <SubHeading>{subHeading}</SubHeading>}
      </Box>
    </Wrapper>
  )

  /**
   * The sandbox banner sits *above* the status banner rather than replacing it (2026-09-22).
   *
   * It replaced it while sandbox was a freeze, because the status banner promised a go-live date
   * that could never fire. Under the full cycle those dates are real and a rehearsal needs them as
   * much as any other term — and on an empty Draft the status banner is the only thing saying why
   * Publish will not work. Stacking is how the page says both: what this term is, then where it is.
   *
   * Deliberately short; the detail belongs to the Sandbox Mode modal, which is where someone acts
   * on it. It is the page's only marker of the mode — the header badge beside the status chip came
   * off on 2026-09-20 as a second thing saying the same thing — so it renders at every status,
   * including the ones a frozen term could never reach.
   */
  const sandboxAlert = slot?.isSandbox === true
    ? renderAlert({
      icon: InfoIcon,
      title: 'This Survey Project is in Sandbox Mode.',
      subHeading: 'It runs normally, but only system-level users can see it or receive its emails, and its results stay out of your reports.'
    })
    : null

  const withSandbox = (statusAlert: JSX.Element): JSX.Element => (
    sandboxAlert == null
      ? statusAlert
      : <>{sandboxAlert}{statusAlert}</>
  )

  // • Renders different UI based on the current slot `status`
  // • Special case for Draft: if there are no sections yet, show a “publish requires sections” alert
  // • Otherwise, fall through to the appropriate UI for other statuses (e.g., Published)
  switch (status) {
    case SlotStatus.Draft:
      if (slotSections?.data?.length === 0) {
        return withSandbox(renderAlert({
          icon: CheckIcon,
          title: 'In order to publish, please add or import Course sections'
        }))
      }
      // A Draft with sections has nothing of its own to say, so on an ordinary term this is the
      // one status that renders no banner at all.
      return sandboxAlert ?? <></>
    case SlotStatus.Published:
      return withSandbox(renderAlert({
        icon: InfoIcon,
        title: `Survey Project will go live on ${getFormattedDate(slot?.slotLiveTimestamp)}`,
        subHeading: 'After this date, you will not be able to revert to draft.'
      }))
    case SlotStatus.Live:
      return withSandbox(renderAlert({
        icon: InfoIcon,
        title: `Survey Project will close: ${getFormattedDate(slot?.slotCloseTimestamp)}`
      }))
    case SlotStatus.Closed:
      return withSandbox(renderAlert({
        icon: InfoIcon,
        title: `Survey Closed: ${getFormattedDate(slot?.slotCloseTimestamp)}`
      }))
    default:
      return <></>
  }
}

const CourseEvaluationAlert: React.FC<CourseEvaluationAlertProps> = ({ slot }) => (
  <SlotStatusComponent slot={slot} />
)

export default CourseEvaluationAlert
